import { useRef, useState } from 'react';
import { Button, Dialog, IconButton, Link, TextField, navigate, useToast, useT } from '@dugzi/ui';
import { BookUser, Cake, Copy, Home, Plus, Tag, Trash2, Upload, UsersRound } from 'lucide-react';
import { api } from '../lib/api';
import { useData } from '../lib/data';
import type { Route } from '../App';

function Item({ to, icon, label, count, on, testId, countLabel }: { to: string; icon: React.ReactNode; label: string; count?: number; on: boolean; testId?: string; countLabel?: string }) {
  return (
    <li>
      <Link to={to} className={`nav__item ${on ? 'is-on' : ''}`} aria-current={on ? 'page' : undefined} data-testid={testId}>
        <span className="nav__icon">{icon}</span><span className="nav__label">{label}</span>
        {count ? <span className="nav__count" aria-label={countLabel ?? `${count} people`}>{count}</span> : null}
      </Link>
    </li>
  );
}

/** Address books (My contacts, Work…), households, labels, and the tidy-up places. */
export function Sidebar({ route, open, onClose, onMove }: { route: Route; open: boolean; onClose(): void; onMove(t: 'import' | 'export' | 'sync'): void }) {
  const t = useT();
  const { books, labels, households, duplicates } = useData();
  const [making, setMaking] = useState(false);
  const total = books.reduce((n, b) => n + b.count, 0);
  return (
    <>
      {open && <div className="nav__scrim" onClick={onClose} aria-hidden />}
      <nav className={`nav ${open ? 'is-open' : ''}`} aria-label="Address books and lists">
        <ul className="nav__list">
          <Item to="/" icon={<UsersRound aria-hidden size={18} />} label="All contacts" count={total} on={route.kind === 'all'} testId="nav-all" />
          <Item to="/birthdays" icon={<Cake aria-hidden size={18} />} label="Upcoming birthdays" on={route.kind === 'birthdays'} testId="nav-birthdays" />
          <Item to="/duplicates" icon={<Copy aria-hidden size={18} />} label="Duplicates" count={duplicates} countLabel={`${duplicates} suggestions`} on={route.kind === 'duplicates'} testId="nav-duplicates" />
          <Item to="/trash" icon={<Trash2 aria-hidden size={18} />} label="Trash" on={route.kind === 'trash'} testId="nav-trash" />
        </ul>
        <div className="nav__head">
          <h2 id="nav-books">Address books</h2>
          <IconButton label={t('launch.new_book')} icon={<Plus aria-hidden size={18} />} onClick={() => setMaking(true)} data-testid="new-book" />
        </div>
        <ul className="nav__list" aria-labelledby="nav-books" data-testid="book-list">
          {books.map((b) => (
            <li key={b.id}>
              <Link to={`/b/${b.id}`} className={`nav__item ${route.kind === 'book' && route.id === b.id ? 'is-on' : ''}`} aria-current={route.kind === 'book' && route.id === b.id ? 'page' : undefined}>
                <span className="nav__icon"><BookUser aria-hidden size={18} /></span>
                <span className="nav__label">{b.name}</span>
                {b.count ? <span className="nav__count" aria-label={`${b.count} people`}>{b.count}</span> : null}
              </Link>
            </li>
          ))}

        </ul>
        <div className="nav__head"><h2 id="nav-households">Households</h2></div>
        <ul className="nav__list" aria-labelledby="nav-households">
          {households.map((h) => <Item key={h.id} to={`/h/${h.id}`} icon={<Home aria-hidden size={16} />} label={h.name} count={h.count} on={route.kind === 'household' && route.id === h.id} />)}
          <Item to="/households" icon={<Plus aria-hidden size={16} />} label={households.length ? 'Manage households' : 'Add a household'} on={route.kind === 'households'} testId="nav-households" />
        </ul>
        {labels.length > 0 && (
          <>
            <div className="nav__head"><h2 id="nav-labels">Labels</h2></div>
            <ul className="nav__list" aria-labelledby="nav-labels" data-testid="label-list">
              {labels.map((l) => <Item key={l.name} to={`/l/${encodeURIComponent(l.name)}`} icon={<Tag aria-hidden size={16} />} label={l.name} count={l.count} on={route.kind === 'label' && route.label === l.name} />)}
            </ul>
          </>
        )}
        <div className="nav__foot">
          <Button size="sm" variant="ghost" icon={<Upload aria-hidden />} onClick={() => onMove('import')} data-testid="open-import">Import or export</Button>
        </div>
      </nav>
      {making && <NewBook onClose={() => setMaking(false)} />}
    </>
  );
}

/** Owner-only creation; the dialog restores focus to its trigger on close. */
function NewBook({ onClose }: { onClose(): void }) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const { reload } = useData();
  const toast = useToast();
  const [name, setName] = useState(() => t('launch.book_name'));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api<{ id: string; name: string }>('/api/books', { body: { name } });
      await reload();
      toast.show(t('launch.book_ready'));
      onClose();
      navigate(`/b/${r.id}`);
    } catch (e) { setError((e as Error).message); input.current?.focus(); }
    setBusy(false);
  };
  return (
    <Dialog open onClose={onClose} title={t('launch.new_book')} actions={<>
      <Button variant="ghost" onClick={onClose}>Cancel</Button>
      <Button variant="primary" loading={busy} onClick={make} data-testid="book-create">Make it</Button>
    </>}>
      <form onSubmit={(e) => { e.preventDefault(); make(); }} className="stack">
        <TextField ref={input} label="Name" value={name} onChange={(e) => setName(e.target.value)} error={error ?? undefined} autoFocus data-testid="book-name"
          hint={t('launch.book_hint')} />
      </form>
    </Dialog>
  );
}
